import { type SelectRootProps, Select as BaseSelect } from '@base-ui/react/select';
import omit from 'lodash-es/omit';
import * as React from 'react';

import type { VariantProp } from '../../common/colors';
import type { DisplayProps } from '../../utilities/shared/Display';
import type {
  ControlMaxWidth,
  ControlMinWidth,
  ControlSize,
  ControlVariantInput,
  ControlWidth,
} from '../shared/types';
import { type IconName, Icon } from '../../assets/Icon/Icon';
import { shadows } from '../../common/shadows';
import {
  getVariantStyles,
  variantAccentColor,
  variantBackgroundColor,
  variantBodyColor,
  variantHeadingColor,
  variantIconColor,
  variantStrokeColor,
} from '../../common/variant';
import { stateZIndexes, zIndexes } from '../../common/z_indexes';
import { colors, darkThemeSelector, styled } from '../../stitches.config';
import { BodySansSizes } from '../../text/Body';
import { LargeSansSizes } from '../../text/Large';
import { SmallSansSizes } from '../../text/Small';
import { displayCSS } from '../../utilities/shared/Display';
import { useControllableState } from '../../utilities/useControllableState';
import {
  ControlProvider,
  useControlMaxWidth,
  useControlMinWidth,
  useControlSize,
  useControlVariant,
  useControlWidth,
} from '../shared/context';
import { selectors, transitions } from '../shared/styles';
import {
  controlArrowsSize,
  controlBorderRadius,
  controlGap,
  controlIconSize,
  controlPaddingX,
  controlPaddingY,
  controlTextDecoration,
  controlTextDecorationValue,
  controlVars,
  getControlSubcontrolSize,
} from '../shared/vars';

// Inline select body colours use the shared `VariantProp` palette directly.

const InlineSelectArrows = styled(Icon, {
  position: 'absolute',
  top: '50%',
  right: controlPaddingX,
  zIndex: stateZIndexes.fixture,
  size: controlArrowsSize,
  color: variantIconColor,
  pointerEvents: 'none',
  opacity: '$$arrowsOpacity',
  transform: 'translateY(-50%)',
});
InlineSelectArrows.displayName = 'InlineSelectArrows';

const InlineSelectIcon = styled(Icon, {
  size: controlIconSize,
  color: variantIconColor,
});
InlineSelectIcon.displayName = 'InlineSelectIcon';

const InlineSelectValue = styled(BaseSelect.Value, {
  width: '100%',
  color: variantBodyColor,
  fontWeight: '$bold',
  textAlign: 'left',
  truncate: true,
  transition: transitions.control,
  textDecoration: controlTextDecoration,

  '&[data-placeholder]': {
    color: variantAccentColor,
  },

  variants: {
    size: {
      'x-small': { ...SmallSansSizes },
      small: { ...SmallSansSizes },
      medium: { ...BodySansSizes },
      large: { ...BodySansSizes },
      'x-large': { ...LargeSansSizes },
    },
  },
});
InlineSelectValue.displayName = 'InlineSelectValue';

const InlineSelectTrigger = styled(BaseSelect.Trigger, {
  zIndex: stateZIndexes.initial,
  display: 'inline-flex',
  alignItems: 'center',
  width: '100%',
  minWidth: 0,
  padding: 0,
  margin: 0,
  background: colors.transparent,
  border: 'none',
  color: variantBodyColor,
  font: 'inherit',
  lineHeight: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',

  [selectors.focus]: {
    outline: 'none',
  },

  '&[data-disabled]': {
    cursor: 'not-allowed',
  },
});
InlineSelectTrigger.displayName = 'InlineSelectTrigger';

const InlineSelectElements = styled('div', {
  position: 'relative',
  zIndex: stateZIndexes.initial,
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: controlGap,
  width: '100%',
  minWidth: 0,
});
InlineSelectElements.displayName = 'InlineSelectElements';

const InlineSelectBoundary = styled('div', {
  position: 'absolute',
  zIndex: stateZIndexes.boundary,
  top: `calc(${controlPaddingY} * -1)`,
  bottom: `calc(${controlPaddingY} * -1)`,
  left: `calc(${controlPaddingX} * -1)`,
  background: '$$boundaryBackground',
  strokeAll: variantStrokeColor,
  borderRadius: controlBorderRadius,
  opacity: '$$boundaryOpacity',
  transition: transitions.control,

  variants: {
    inlineChevrons: {
      true: {
        right: `calc((${controlPaddingX}) * -1)`,
      },
      false: {
        right: `calc((${controlPaddingX} + ${controlArrowsSize} + ${controlGap}) * -1)`,
      },
    },
  },
});
InlineSelectBoundary.displayName = 'InlineSelectBoundary';

const InlineSelectContainer = styled('div', {
  position: 'relative',
  zIndex: stateZIndexes.initial,
  display: 'flex',
  maxWidth: '100%',
  transition: transitions.control,
  cursor: 'pointer',
  $$boundaryBackground: colors.bgApplicationLight,
  $$boundaryOpacity: 0,
  $$arrowsOpacity: 0,
  [controlTextDecoration]: controlTextDecorationValue,

  [darkThemeSelector]: {
    $$boundaryBackground: colors.bgApplicationDark,
  },

  [selectors.hover]: {
    zIndex: stateZIndexes.hover,
  },

  [selectors.focus]: {
    zIndex: stateZIndexes.focus,
    outline: 'none',

    [`& ${InlineSelectBoundary}`]: {
      boxShadow: shadows.focusRingLight,

      [darkThemeSelector]: {
        boxShadow: shadows.focusRingDark,
      },
    },
  },

  [selectors.interactive]: {
    $$boundaryOpacity: 1,
    $$arrowsOpacity: 1,
    [controlTextDecoration]: 'none',
  },

  '&:has([data-popup-open])': {
    zIndex: stateZIndexes.focus,
    $$boundaryOpacity: 1,
    $$arrowsOpacity: 1,
    [controlTextDecoration]: 'none',
  },

  variants: {
    disabled: {
      true: {
        opacity: 0.5,
        pointerEvents: 'none',
      },
      false: {},
    },
    size: {
      'x-small': {
        ...controlVars.size['x-small'],
        ...SmallSansSizes,
      },
      small: {
        ...controlVars.size.small,
        ...SmallSansSizes,
      },
      medium: {
        ...controlVars.size.medium,
        ...BodySansSizes,
      },
      large: {
        ...controlVars.size.large,
        ...BodySansSizes,
      },
      'x-large': {
        ...controlVars.size['x-large'],
        ...LargeSansSizes,
      },
    },
    variant: {
      alternative: { ...getVariantStyles('alternative') },
      attention: { ...getVariantStyles('attention') },
      brand: { ...getVariantStyles('brand') },
      neutral: { ...getVariantStyles('neutral') },
      negative: { ...getVariantStyles('negative') },
      positive: { ...getVariantStyles('positive') },
    },
    invalid: {
      true: {
        ...getVariantStyles('negative'),
      },
      false: {},
    },
  },
});
InlineSelectContainer.displayName = 'InlineSelectContainer';

const InlineSelectItemText = styled(BaseSelect.ItemText, {
  color: '$$itemTextColor',
  fontWeight: '$bold',

  variants: {
    size: {
      'x-small': { ...SmallSansSizes },
      small: { ...SmallSansSizes },
      medium: { ...BodySansSizes },
      large: { ...BodySansSizes },
      'x-large': { ...LargeSansSizes },
    },
    variant: {
      alternative: { ...getVariantStyles('alternative') },
      attention: { ...getVariantStyles('attention') },
      brand: { ...getVariantStyles('brand') },
      neutral: { ...getVariantStyles('neutral') },
      negative: { ...getVariantStyles('negative') },
      positive: { ...getVariantStyles('positive') },
    },
  },
});
InlineSelectItemText.displayName = 'InlineSelectItemText';

const InlineSelectItemRoot = styled(BaseSelect.Item, {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  gap: '$8',
  padding: '$4 $8',
  borderRadius: '$6',
  outline: 'none',
  cursor: 'pointer',
  userSelect: 'none',
  $$itemTextColor: variantHeadingColor,

  '&[data-highlighted]': {
    backgroundColor: variantBackgroundColor,
  },

  '&[data-selected]': {
    backgroundColor: colors.bgBrandLight,
    $$itemTextColor: colors.headingBrandLight,

    [darkThemeSelector]: {
      backgroundColor: colors.bgBrandDark,
      $$itemTextColor: colors.headingBrandDark,
    },
  },

  '&[data-disabled]': {
    opacity: 0.5,
    cursor: 'not-allowed',
  },

  variants: {
    size: {
      'x-small': { ...SmallSansSizes },
      small: { ...SmallSansSizes },
      medium: { ...BodySansSizes },
      large: { ...BodySansSizes },
      'x-large': { ...LargeSansSizes },
    },
    variant: {
      alternative: { ...getVariantStyles('alternative') },
      attention: { ...getVariantStyles('attention') },
      brand: { ...getVariantStyles('brand') },
      neutral: { ...getVariantStyles('neutral') },
      negative: { ...getVariantStyles('negative') },
      positive: { ...getVariantStyles('positive') },
    },
  },
});
InlineSelectItemRoot.displayName = 'InlineSelectItemRoot';

export interface InlineSelectItemProps {
  children?: React.ReactNode;
  /**
   * Whether this item is disabled.
   */
  disabled?: boolean;
  /**
   * Plain-text label used for keyboard type-ahead. Falls back to `children` text content.
   */
  label?: string;
  /**
   * The size of the item.
   */
  size?: ControlSize;
  /**
   * Unique value for this item. Matched against `value`/`defaultValue` on `InlineSelect`
   * to determine selection state.
   */
  value: string;
  /**
   * The variant of the item.
   */
  variant?: VariantProp;
}

export function InlineSelectItem({
  children,
  disabled,
  label,
  size,
  value,
  variant = 'neutral',
}: InlineSelectItemProps) {
  const effectiveSize = useControlSize(size, 'medium');

  return (
    <InlineSelectItemRoot
      value={value}
      label={label}
      disabled={disabled}
      size={effectiveSize}
      variant={variant}
    >
      <InlineSelectItemText size={effectiveSize} variant={variant}>
        {children}
      </InlineSelectItemText>
    </InlineSelectItemRoot>
  );
}

const InlineSelectGroup = styled(BaseSelect.Group, {
  display: 'flex',
  flexDirection: 'column',
  gap: '$2',

  '& + &': {
    marginTop: '$4',
  },
});
InlineSelectGroup.displayName = 'InlineSelectGroup';

const InlineSelectGroupLabel = styled(BaseSelect.GroupLabel, {
  ...SmallSansSizes,
  padding: '$4 $8 0',
  fontWeight: '$medium',
  color: colors.controlContentPlaceholderLight,

  [darkThemeSelector]: {
    color: colors.controlContentPlaceholderDark,
  },
});
InlineSelectGroupLabel.displayName = 'InlineSelectGroupLabel';

const InlineSelectList = styled(BaseSelect.List, {
  display: 'flex',
  flexDirection: 'column',
  outline: 'none',
});
InlineSelectList.displayName = 'InlineSelectList';

const InlineSelectPopup = styled(BaseSelect.Popup, {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  padding: '$6 $8',
  minWidth: 'var(--anchor-width)',
  overflow: 'auto',
  backgroundColor: colors.bgApplicationLight,
  borderRadius: '$10',
  boxShadow: shadows.overlayLight,
  transformOrigin: 'var(--transform-origin)',
  outline: 'none',

  [darkThemeSelector]: {
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },

  '@media (prefers-reduced-motion: no-preference)': {
    transition: 'opacity 150ms ease-out, transform 150ms ease-out',

    '&[data-starting-style], &[data-ending-style]': {
      opacity: 0,
      transform: 'scale(0.96)',
    },
  },
});
InlineSelectPopup.displayName = 'InlineSelectPopup';

const InlineSelectPositioner = styled(BaseSelect.Positioner, {
  outline: 'none',
  zIndex: zIndexes.overlay,
});
InlineSelectPositioner.displayName = 'InlineSelectPositioner';

const InlineSelectPortal = styled(BaseSelect.Portal);
InlineSelectPortal.displayName = 'InlineSelectPortal';

const InlineSelectRoot = styled(BaseSelect.Root);
InlineSelectRoot.displayName = 'InlineSelectRoot';

type PositionerProps = React.ComponentProps<typeof BaseSelect.Positioner>;

type InlineSelectInheritedRootProps = SelectRootProps<string, false>;

export interface InlineSelectProps
  extends DisplayProps,
    Omit<InlineSelectInheritedRootProps, 'onValueChange' | 'children' | 'items'> {
  /**
   * Items for Base UI (`Select.Root` labels) and for the dropdown list.
   *
   * **`Record`** for static maps, **`{ value, label }[]`** for ordered options, or **`{ label?, items }[]`**
   * for grouped sections (rendered as `Select.Group`).
   */
  items: NonNullable<InlineSelectInheritedRootProps['items']>;
  /**
   * Called when the selected value changes (`eventDetails` from Base UI is not surfaced here).
   */
  onValueChange?: (value: string | null) => void;
  /**
   * Accessible label for the trigger when no visible label exists.
   */
  'aria-label'?: string;
  /**
   * ID of an element that labels the trigger.
   */
  'aria-labelledby'?: string;
  /**
   * Optional leading icon, rendered next to the selected value.
   */
  icon?: IconName;
  /**
   * Whether to display the trigger in an indeterminate state (e.g. for a multi-row form
   * where selections disagree). Shows an em-dash by default, or the placeholder when
   * `showPlaceholderForIndeterminate` is set.
   */
  indeterminate?: boolean;
  /**
   * Whether to display the chevrons inside the trigger or inside the boundary.
   */
  inlineChevrons?: boolean;
  /**
   * Whether the select is invalid. Applies the `negative` variant palette.
   */
  invalid?: boolean;
  /**
   * Props forwarded to the underlying `Select.Positioner`. Use for `sideOffset`, `align`,
   * `collisionAvoidance`, etc. Replaces the previous `popoverProps` (Base UI splits positioning
   * from presentation so this is the appropriate target).
   */
  positionerProps?: Omit<PositionerProps, 'children' | 'className' | 'style'>;
  /**
   * Copy for the empty selection so the inline dotted underline is visible; without it the
   * trigger can look like plain body text.
   */
  placeholder: string;
  /**
   * When combined with `indeterminate`, show the placeholder string instead of the
   * default em-dash.
   */
  showPlaceholderForIndeterminate?: boolean;
  /**
   * The size of the inline trigger (controls text and spacing).
   */
  size?: ControlSize;
  /**
   * Optional text (or node) rendered after the value, inline with the trigger.
   */
  suffix?: React.ReactNode;
  /**
   * Colour variant for value/icon/stroke using the design system semantic palettes.
   */
  variant?: ControlVariantInput;
  /**
   * Constrains the container width. Accepts any CSS width string or number.
   */
  width?: ControlWidth;
  /**
   * Constrains the container maxWidth.
   */
  maxWidth?: ControlMaxWidth;
  /**
   * Constrains the container minWidth.
   */
  minWidth?: ControlMinWidth;
}

type SelectItemsProp = Exclude<InlineSelectInheritedRootProps['items'], undefined>;

const INLINE_SELECT_PROPS_WITHOUT_ROOT = [
  'aria-label',
  'aria-labelledby',
  'children',
  'defaultValue',
  'display',
  'icon',
  'items',
  'indeterminate',
  'invalid',
  'inlineChevrons',
  'maxWidth',
  'minWidth',
  'onValueChange',
  'placeholder',
  'positionerProps',
  'showPlaceholderForIndeterminate',
  'size',
  'suffix',
  'value',
  'variant',
  'width',
];

type SelectGroupShape = { items: ReadonlyArray<unknown>; label?: React.ReactNode };

function itemValueKey(row: unknown): string {
  if (row != null && typeof row === 'object' && 'value' in row) {
    return String((row as { value: unknown }).value);
  }
  return String(row);
}

function stableGroupReactKey(group: SelectGroupShape): string {
  const labelPart = typeof group.label === 'string' ? group.label : '';
  const valuesPart = group.items.map(itemValueKey).join('|');
  if (labelPart !== '' || valuesPart !== '') {
    return `${labelPart}:${valuesPart}`;
  }
  return 'group:empty';
}

function isGroupedSelectItems(items: SelectItemsProp): boolean {
  if (!Array.isArray(items) || items.length === 0) return false;
  const first = items[0];
  return (
    first != null &&
    typeof first === 'object' &&
    'items' in first &&
    Array.isArray((first as { items: unknown }).items)
  );
}

function inlineSelectItemFromRow(row: unknown, reactKey: string): React.ReactElement {
  if (row != null && typeof row === 'object' && 'value' in row) {
    const o = row as { value: unknown; label?: React.ReactNode };
    const itemValue = String(o.value);
    const labelNode = o.label ?? itemValue;
    return (
      <InlineSelectItem
        key={reactKey}
        value={itemValue}
        label={typeof labelNode === 'string' ? labelNode : undefined}
      >
        {labelNode}
      </InlineSelectItem>
    );
  }

  const itemValue = String(row);
  return (
    <InlineSelectItem key={reactKey} value={itemValue}>
      {itemValue}
    </InlineSelectItem>
  );
}

/** List UI for portaled dropdown; matches `items` shape passed to Base UI Root. */
function listChildrenFromItems(items: SelectItemsProp): React.ReactNode {
  if (!Array.isArray(items)) {
    return Object.entries(items).map(([itemValue, label]) =>
      inlineSelectItemFromRow({ value: itemValue, label }, itemValue),
    );
  }

  if (isGroupedSelectItems(items)) {
    return items.map((group) => {
      const g = group as SelectGroupShape;
      return (
        <InlineSelectGroup key={stableGroupReactKey(g)}>
          {g.label != null ? <InlineSelectGroupLabel>{g.label}</InlineSelectGroupLabel> : null}
          {g.items.map((row) => inlineSelectItemFromRow(row, itemValueKey(row)))}
        </InlineSelectGroup>
      );
    });
  }

  return items.map((row) => inlineSelectItemFromRow(row, itemValueKey(row)));
}

export const InlineSelect = React.forwardRef<HTMLButtonElement, InlineSelectProps>((props, ref) => {
  const {
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    defaultValue,
    disabled,
    display,
    icon,
    id,
    items: itemsProp,
    indeterminate,
    invalid,
    inlineChevrons = false,
    maxWidth,
    minWidth,
    placeholder,
    positionerProps,
    showPlaceholderForIndeterminate,
    size,
    variant,
    width,
    onValueChange,
    value,
  } = props;

  const rootForwarded = omit(props, [...INLINE_SELECT_PROPS_WITHOUT_ROOT]);

  const controlSize = useControlSize(size, 'medium');
  const effectiveSubcontrolSize = getControlSubcontrolSize(controlSize);
  const controlVariant = useControlVariant(variant, 'neutral');
  const controlWidth = useControlWidth(width);
  const controlMaxWidth = useControlMaxWidth(maxWidth);
  const controlMinWidth = useControlMinWidth(minWidth);

  const itemsForRoot = itemsProp;
  const listContent = listChildrenFromItems(itemsProp);

  const [resolvedValue, setResolvedValue] = useControllableState<string | null>({
    defaultValue: defaultValue ?? null,
    onChange: onValueChange,
    value,
  });

  const showChevron = !disabled;

  const valueRenderer = indeterminate
    ? () => (showPlaceholderForIndeterminate ? placeholder : <>&ndash;&nbsp;</>)
    : undefined;

  const containerCSS = displayCSS(display);

  const chevrons = (
    <ControlProvider value={{ size: effectiveSubcontrolSize }}>
      <InlineSelectArrows icon="chevrons-vertical" />
    </ControlProvider>
  );

  return (
    <InlineSelectRoot
      {...(rootForwarded as React.ComponentPropsWithoutRef<typeof BaseSelect.Root>)}
      value={resolvedValue}
      onValueChange={(next) => setResolvedValue(next as string | null)}
      items={itemsForRoot}
    >
      <InlineSelectContainer
        size={controlSize}
        variant={controlVariant}
        disabled={disabled ?? false}
        invalid={invalid ?? false}
        style={{
          width: controlWidth,
          maxWidth: controlMaxWidth,
          minWidth: controlMinWidth,
        }}
        css={containerCSS}
      >
        <InlineSelectElements>
          <ControlProvider value={{ size: effectiveSubcontrolSize }}>
            {icon && <InlineSelectIcon icon={icon} />}
          </ControlProvider>
          <InlineSelectTrigger
            ref={ref}
            id={id}
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
          >
            <InlineSelectValue size={controlSize} placeholder={placeholder}>
              {valueRenderer}
            </InlineSelectValue>
          </InlineSelectTrigger>
        </InlineSelectElements>
        {showChevron && inlineChevrons ? chevrons : null}
        <InlineSelectBoundary inlineChevrons={inlineChevrons}>
          {showChevron && !inlineChevrons ? chevrons : null}
        </InlineSelectBoundary>
      </InlineSelectContainer>
      <InlineSelectPortal>
        <InlineSelectPositioner {...positionerProps}>
          <InlineSelectPopup>
            <InlineSelectList>
              <ControlProvider value={{ size: controlSize }}>{listContent}</ControlProvider>
            </InlineSelectList>
          </InlineSelectPopup>
        </InlineSelectPositioner>
      </InlineSelectPortal>
    </InlineSelectRoot>
  );
});
InlineSelect.displayName = 'InlineSelect';
